import {
  Button,
  Divider,
  Group,
  GroupPosition,
  LoadingOverlay,
  MantineNumberSize,
  MantineSize,
  Stack,
  SystemProp,
} from "@mantine/core";
import React, {
  CSSProperties,
  PropsWithChildren,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { IFocusableForm } from "@type";
import { FormSubmitButton } from "./FormSubmitButton";
import { useNotification, useRouterContext } from "@hooks";
import { useApiErrorHandler } from "@hooks";

export interface IFormProps<Values = Record<string, unknown>> {
  form: IFocusableForm<Partial<Values>>;
  loading?: boolean;
  readOnly?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  readyToLoad?: boolean;
  allowSubmitWithNoChanges?: boolean;
  fetchData?: () => Promise<Values | undefined>;
  onSubmit?: (values: Values) => Promise<any>;
  afterSubmit?: () => void;
  onCancel?: () => void;
  spacing?: MantineNumberSize;
  align?: CSSProperties["alignItems"];
  justify?: CSSProperties["justifyContent"];
  mih?: SystemProp<CSSProperties["minHeight"]>;
  mah?: SystemProp<CSSProperties["maxHeight"]>;
  buttonPosition?: GroupPosition;
  buttonSize?: MantineSize;
  controlRef?: Ref<IRefreshableForm>;
}

export interface IRefreshableForm {
  refresh(): void;
}

export const FocusableForm: <Values = Record<string, unknown>>(
  props: PropsWithChildren<IFormProps<Values>>,
) => JSX.Element = ({
  form,
  loading,
  readOnly,
  submitLabel,
  cancelLabel,
  allowSubmitWithNoChanges,
  readyToLoad,
  fetchData,
  onSubmit,
  afterSubmit,
  onCancel,
  spacing,
  align,
  justify,
  mih,
  mah,
  buttonPosition,
  buttonSize,
  controlRef,
  children,
}) => {
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { open: notify } = useNotification();

  const apiErrorHandler = useApiErrorHandler(notify, form.setErrors);
  const { useBlocker } = useRouterContext();

  const isDirty = useRef(false);
  isDirty.current = form.isDirty();

  const loadForm = useCallback(() => {
    if (!fetchData) {
      return;
    }

    setFetching(true);

    fetchData()
      .then((result) => {
        if (result) {
          form.setValues(result);
          form.resetDirty(result);
        }
      })
      .catch(apiErrorHandler)
      .finally(() => {
        setFetching(false);
      });
  }, [fetchData]);

  useImperativeHandle(controlRef, () => ({
    refresh() {
      loadForm();
    },
  }));

  const submitForm = useCallback(
    (data: any) => {
      if (!onSubmit) {
        return;
      }

      setSubmitting(true);

      const wasDirty = form.isDirty();

      onSubmit(data)
        .then((result) => {
          if (wasDirty) {
            form.resetDirty(data);
            isDirty.current = false;
            notify?.({
              type: "success",
              message: result?.SuccessMessage || "Changes saved.",
              description: "",
            });
          }
          if (afterSubmit) {
            afterSubmit();
          }
        })
        .catch(apiErrorHandler)
        .finally(() => {
          setSubmitting(false);
        });
    },
    [onSubmit, afterSubmit],
  );

  useEffect(() => {
    if (!fetchData) {
      setFetching(false);
    } else if (readyToLoad) {
      loadForm();
    }
  }, [readyToLoad, fetchData]);

  useBlocker(
    () =>
      isDirty.current && !window.confirm("You have unsaved changes. Continue?"),
  );

  return (
    <>
      <LoadingOverlay visible={fetching || loading || false} />
      {!loading && !fetching && (
        <form onSubmit={onSubmit ? form.onSubmit(submitForm) : undefined}>
          <Stack
            spacing={spacing || "lg"}
            align={align}
            justify={justify}
            mih={mih}
            mah={mah}
          >
            {children}
            {!readOnly && (
              <>
                <Divider my="sm" />

                <Group position={buttonPosition || "left"}>
                  {onCancel && (
                    <Button
                      size={buttonSize}
                      variant="outline"
                      onClick={onCancel}
                    >
                      {cancelLabel || "Cancel"}
                    </Button>
                  )}
                  <FormSubmitButton
                    size={buttonSize}
                    loading={submitting}
                    disabled={!allowSubmitWithNoChanges && !form.isDirty()}
                  >
                    {submitLabel || "Save"}
                  </FormSubmitButton>
                </Group>
              </>
            )}
          </Stack>
        </form>
      )}
    </>
  );
};
